import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useCart } from '../context/CartContext';
import {
  parsePresentations,
  formatAmount,
  formatQtyUnit,
  formatStock,
  bestSizeIdx,
  getPrincipalSize,
  baseUnits,
} from '../utils/presentations';

/* Paleta del sitio (dark-glass + ámbar). El prototipo usaba un accent por
   producto; aquí usamos el ámbar de marca para todo. */
const ACCENT = '#fbbf24'; // amber-400
const FILL_TOP = '#fde68a'; // amber-200
const FILL_BOT = '#f59e0b'; // amber-500

const mxn = (n) =>
  '$' + (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mxnShort = (n) => mxn(n).replace('.00', '');

/* Dimensiones físicas del recipiente por presentación (crecen izq→der). */
const VESSEL = {
  ml: [{ w: 16, h: 26 }, { w: 20, h: 35 }, { w: 26, h: 46 }, { w: 32, h: 58 }],
  g: [{ w: 20, h: 22 }, { w: 25, h: 30 }, { w: 30, h: 40 }, { w: 36, h: 50 }],
};
const FILL = 0.86;

const fillBg = (selected) =>
  selected
    ? `linear-gradient(180deg, ${ACCENT}, ${FILL_BOT})`
    : `linear-gradient(180deg, ${FILL_TOP}cc, ${FILL_BOT}cc)`;

/* -------------------------------------------------------------- Vessels --- */

function VesselGlass({ w, h, selected }) {
  const rim = Math.max(3, Math.round(h * 0.1));
  return (
    <div style={{ width: w, height: h, position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          clipPath: 'polygon(14% 5%, 86% 5%, 76% 99%, 24% 99%)',
          overflow: 'hidden',
          background: selected ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.11)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: `${FILL * 100}%`,
            background: fillBg(selected),
          }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,.6)' }} />
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          top: 1,
          left: '13%',
          right: '13%',
          height: rim,
          borderRadius: '50%',
          border: `1.5px solid ${selected ? `${ACCENT}e6` : 'rgba(255,255,255,.55)'}`,
          background: 'rgba(255,255,255,.06)',
        }}
      />
    </div>
  );
}

function VesselContainer({ w, h, selected }) {
  return (
    <div
      style={{
        width: w,
        height: h,
        borderRadius: '5px 5px 8px 8px',
        position: 'relative',
        overflow: 'hidden',
        background: selected ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.08)',
        border: `1px solid ${selected ? `${ACCENT}cc` : 'rgba(255,255,255,0.24)'}`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: `${FILL * 100}%`,
          background: fillBg(selected),
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 1, right: 1, height: 2, background: 'rgba(255,255,255,.5)' }} />
      </div>
    </div>
  );
}

function VesselPieces({ amount, selected, scale = 1 }) {
  const n = Math.min(Number(amount) || 1, 4);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: Math.max(2, 3 * scale) }}>
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 22 * scale,
            height: 8 * scale,
            borderRadius: 3 * scale,
            background: fillBg(selected),
            border: `1px solid ${selected ? `${ACCENT}99` : 'rgba(255,255,255,0.2)'}`,
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,.25)',
          }}
        />
      ))}
    </div>
  );
}

function Vessel({ unit, idx, amount, selected, scale = 1 }) {
  if (unit === 'pza' || !unit) return <VesselPieces amount={amount} selected={selected} scale={scale} />;
  const dims = (unit === 'ml' ? VESSEL.ml : VESSEL.g)[idx] || VESSEL.ml[0];
  return unit === 'ml'
    ? <VesselGlass w={dims.w * scale} h={dims.h * scale} selected={selected} />
    : <VesselContainer w={dims.w * scale} h={dims.h * scale} selected={selected} />;
}

/* ------------------------------------------------------------- FanNode ---- */
/* Cascada diagonal del abanico (Versión A): offsets [dx,dy] en px desde el
   centro del botón "+N", de arriba-derecha hacia abajo-izquierda. */
const FAN_OFFSETS = {
  1: [[-140, -120]],
  2: [[-92, -160], [-180, -55]],
  3: [[-50, -200], [-132, -114], [-214, -28]],
};
const BUB_D = 108; // diámetro de la burbuja

function FanNode({ size, idx, unit, isBest, x, y, delay = 0, disabled, onPick }) {
  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={onPick}
      initial={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
      animate={{ opacity: 1, scale: 1, x, y }}
      exit={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
      transition={{ type: 'spring', stiffness: 340, damping: 24, delay }}
      whileTap={disabled ? undefined : { scale: 1.08 }}
      aria-label={`Agregar ${size.label}, ${formatAmount(size.qty, unit)}, ${mxnShort(size.price)}`}
      className={`absolute left-1/2 top-1/2 rounded-full flex flex-col items-center justify-center gap-0 px-2 z-30 focus:outline-none focus:ring-2 focus:ring-amber-300/70 ${isBest ? 'lj-fan-glow' : ''}`}
      style={{
        width: BUB_D,
        height: BUB_D,
        marginLeft: -BUB_D / 2,
        marginTop: -BUB_D / 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: 'rgba(20,15,16,0.93)',
        border: `1px solid ${isBest ? ACCENT : 'rgba(255,255,255,0.16)'}`,
        boxShadow: isBest ? undefined : '0 16px 34px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.08)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {isBest && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-[23px] h-[23px] rounded-full grid place-items-center text-amber-950" style={{ background: ACCENT, boxShadow: '0 3px 8px rgba(0,0,0,.45)' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l2.5 5.5L20 9.3l-4 4 1 5.7-5-2.8-5 2.8 1-5.7-4-4 5.5-.8z" /></svg>
        </span>
      )}
      <Vessel unit={unit} idx={idx} amount={size.qty} selected={isBest} scale={0.6} />
      <div className="text-[12.5px] font-bold leading-none mt-1 text-white">{size.label}</div>
      <div className="font-extrabold text-[18px] leading-tight" style={{ color: ACCENT }}>
        {mxnShort(size.price)}
      </div>
      <div className="text-[9px] font-semibold leading-none mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
        {formatAmount(size.qty, unit)}
      </div>
    </motion.button>
  );
}

/* ----------------------------------------------------------- ProductCard -- */

const ProductCard = ({
  image,
  title,
  description,
  price,
  id,
  availability,
  applyPromotions,
  presentations,
  unitOfMeasure,
  categoryId,
  orderingOpen = true,
  comboFromPrice = null,
  onArmarCombo,
  fanOpen = false,
  onFanToggle,
  onFanClose,
}) => {
  const { addItem, items } = useCart();

  const sizes = useMemo(
    () => parsePresentations({ presentations, unit_of_measure: unitOfMeasure }),
    [presentations, unitOfMeasure]
  );
  const unit = unitOfMeasure || sizes[0]?.unit || '';
  const comboSize = sizes.find((s) => s.isComboUnit) || null; // porción
  // Protagonista de la card: el combo si hay porción; si no, el tamaño por el
  // que normalmente se vende (el mayor: 1 litro en los caldos). Sin esto los
  // platillos sin porción caían al fallback legacy, que anunciaba el tamaño más
  // barato y agregaba al carrito el precio viejo de items.price.
  const mainSize = comboSize || getPrincipalSize(sizes);
  const otherSizes = sizes.filter((s) => s !== mainSize); // el resto va al abanico
  const hasSizes = sizes.length > 0;
  // El botón protagonista abre el chooser cuando hay porción. Con descuento es
  // un combo de precio fijo ($99 el más barato); sin descuento ("plato") se
  // cobra la suma de las porciones. Por eso el precio que se anuncia NO es el
  // de la porción: en combo sería mentir (la porción cuesta menos que el combo).
  const isCombo = !!comboSize;
  const isPlato = isCombo && Number(applyPromotions) !== 1;
  // El mejor precio por medida se compara contra TODOS los tamaños: si el
  // protagonista ya es el más rendidor, ningún nodo del abanico se rotula.
  const bestIdx = bestSizeIdx(sizes, unit);
  const bestSize = bestIdx >= 0 ? sizes[bestIdx] : null;

  const isAvailable = availability > 0;
  // Modo vitrina (orderingOpen=false): la card se ve completa —incluido el
  // stock— pero no deja agregar nada al carrito.
  const canOrder = isAvailable && orderingOpen;

  // Porciones de este producto ya reservadas en el carrito (todas las líneas).
  // El stock (product_counter / availability) se cuenta en unidades de la
  // presentación protagonista —porciones, casi siempre— y cada tamaño consume
  // las suyas: un grande de 800 g con porción de 200 g se lleva 4. Es la misma
  // cuenta que hace el backend al descontar; si aquí se contara 1 por renglón,
  // la card ofrecería tamaños que la caja va a rechazar.
  const unidadesDe = useMemo(
    () => (presentationKey, cantidad = 1) =>
      baseUnits({ presentations, unit_of_measure: unitOfMeasure }, cantidad, presentationKey),
    [presentations, unitOfMeasure]
  );
  const reservedUnits = useMemo(
    () =>
      (items || []).reduce((sum, it) => {
        if (it.id !== id) return sum;
        return sum + baseUnits(
          { presentations, unit_of_measure: unitOfMeasure },
          it.quantity || 0,
          it.presentation || null
        );
      }, 0),
    [items, id, presentations, unitOfMeasure]
  );
  const remainingUnits = Math.max(0, availability - reservedUnits);
  // Cuánto se lleva un tamaño concreto, para apagar sólo los que no caben.
  const cabe = (size) => remainingUnits >= unidadesDe(size?.key ?? null);

  const buildPayload = (size) => ({
    id,
    image,
    title,
    description,
    price: size ? size.price : parseFloat(price) || 0,
    basePrice: price,
    availability,
    applyPromotions,
    categoryId,
    presentation: size ? size.key : null,
    presentationLabel: size ? size.label : null,
    qty: size ? size.qty : null,
    unit: size ? size.unit : null,
    // Unidades de contador que come CADA pieza de esta línea. El carrito lo
    // necesita para topar el stock sin volver a parsear las presentaciones.
    unitsPerItem: unidadesDe(size ? size.key : null),
  });

  const addSize = (size) => {
    if (!canOrder) return;
    addItem(buildPayload(size));
  };

  const handleCombo = () => {
    if (!canOrder) return;
    if (typeof onArmarCombo === 'function') {
      // applyPromotions decide si el flujo aplica descuento de combo o arma un
      // "plato" a precio lleno (suma de porciones, sin descuento).
      onArmarCombo({ id, title, description, image, categoryId, applyPromotions });
    }
  };

  // Solo lo usa el fallback legacy (item sin presentaciones), donde el unico
  // precio posible es el del item.
  const minPrice = parseFloat(price) || 0;

  const fanOffsets = FAN_OFFSETS[Math.min(otherSizes.length, 3)] || [];

  return (
    <motion.div
      className="group relative w-full h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{ zIndex: fanOpen ? 30 : 'auto' }}
    >
      {/* Scrim leve (Versión A — "sin oscurecer"): apenas un velo sobre la card
          para asentar las burbujas, que flotan encima (nodos z-30, velo z-[15]). */}
      <AnimatePresence>
        {fanOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => onFanClose && onFanClose()}
            className="absolute inset-0 rounded-3xl z-[15] cursor-pointer"
            style={{ background: 'rgba(10,6,8,0.22)' }}
          />
        )}
      </AnimatePresence>

      {/* Imagen con nombre + descripción sobrepuestos */}
      <div className="relative aspect-[16/10] rounded-3xl overflow-hidden border border-white/10 group-hover:border-white/20 transition-colors duration-500 bg-gradient-to-br from-white/5 to-black/30">
        {/* Badges */}
        <div className="absolute top-3 left-3 right-3 flex justify-between items-start z-[7] pointer-events-none">
          {applyPromotions === 1 ? (
            <span className="text-xs font-medium text-white px-2.5 py-1 rounded-full backdrop-blur-sm" style={{ background: `linear-gradient(90deg, ${ACCENT}, ${FILL_BOT})`, color: '#3a2a05' }}>
              Promo
            </span>
          ) : <span />}
          <span
            className={`text-xs font-medium text-white px-2.5 py-1 rounded-full backdrop-blur-sm ${isAvailable ? 'bg-black/80 border border-gray-700/50' : 'bg-red-500/90 border border-red-400/50'}`}
          >
            {isAvailable ? formatStock(availability) : 'Agotado'}
          </span>
        </div>

        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-white/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Scrim + nombre */}
        <div className="absolute left-0 right-0 bottom-0 z-[6] px-3.5 pt-8 pb-3 pointer-events-none bg-gradient-to-b from-transparent via-black/45 to-black/85">
          <h3 className="text-white font-semibold text-base leading-tight line-clamp-2">{title || 'Producto'}</h3>
          {description && <p className="text-white/70 text-xs leading-snug mt-0.5 line-clamp-1">{description}</p>}
        </div>
      </div>

      {/* Selector */}
      <div className="mt-3">
        {hasSizes ? (
          <div className="relative" style={{ zIndex: fanOpen ? 30 : 'auto' }}>
            {/* backdrop cuando el abanico está abierto */}
            <AnimatePresence>
              {fanOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => onFanClose && onFanClose()}
                  className="fixed inset-0 z-20"
                  style={{ background: 'transparent' }}
                />
              )}
            </AnimatePresence>

            <div className="flex gap-2 items-stretch relative">
              {/* PROTAGONISTA: el combo, o el tamaño por el que se vende normalmente */}
              <button
                type="button"
                onClick={comboSize ? handleCombo : () => addSize(mainSize)}
                disabled={!canOrder || (!comboSize && !cabe(mainSize))}
                className="relative z-[2] flex-1 min-w-0 flex items-center gap-3 px-3 py-3 rounded-2xl text-left focus:outline-none focus:ring-2 focus:ring-amber-300/70 disabled:opacity-50 cursor-pointer"
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}40, ${ACCENT}0d)`,
                  border: `1.5px solid ${ACCENT}a6`,
                  boxShadow: `0 14px 30px -16px ${ACCENT}d9`,
                }}
                aria-label={
                  isPlato
                    ? `Armar plato con ${title}`
                    : isCombo
                      ? `Armar combo con ${title}${comboFromPrice ? `, desde ${mxnShort(comboFromPrice)}` : ''}`
                      : `Agregar ${title} ${formatQtyUnit(mainSize)} al carrito`
                }
              >
                <span className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0 text-amber-950" style={{ background: ACCENT }}>
                  {comboSize ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
                      <line x1="12" y1="4.5" x2="12" y2="19.5" />
                      <line x1="12" y1="12" x2="21.5" y2="12" />
                      <circle cx="7.2" cy="12" r="2.3" fill="currentColor" stroke="none" />
                      <circle cx="16.75" cy="8.25" r="1.35" fill="currentColor" stroke="none" />
                      <circle cx="16.75" cy="15.75" r="1.35" fill="currentColor" stroke="none" />
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-white font-extrabold text-base leading-tight">
                    {isPlato ? 'Arma tu plato' : isCombo ? 'Combo' : mainSize.label}
                  </span>
                  <span className="block text-white/50 text-[11px] mt-0.5">
                    {isPlato ? 'Pagas cada porción' : isCombo ? 'Elige tu combinación' : formatQtyUnit(mainSize)}
                  </span>
                </span>
                {isCombo && !isPlato ? (
                  /* El precio fijo del combo vive en el chooser; aquí sólo el
                     ancla "desde". Sin catálogo cargado, una flecha de "abre". */
                  comboFromPrice ? (
                    <span className="flex flex-col items-end flex-shrink-0 leading-none">
                      <span className="text-white/60 text-[10px] font-semibold uppercase tracking-wide">Desde</span>
                      <span className="font-extrabold text-lg text-white mt-1">{mxnShort(comboFromPrice)}</span>
                    </span>
                  ) : (
                    <svg className="w-5 h-5 text-white/50 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )
                ) : (
                  <span className="font-extrabold text-lg text-white">{mxnShort(mainSize.price)}</span>
                )}
              </button>

              {/* MÁS — abre abanico */}
              {otherSizes.length > 0 && (
                <div className="relative z-[40] flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => onFanToggle && onFanToggle()}
                    aria-label={fanOpen ? 'Ocultar tamaños' : 'Más tamaños'}
                    aria-expanded={fanOpen}
                    className="relative z-[45] w-[58px] h-full min-h-[64px] flex flex-col items-center justify-center gap-1 p-1.5 rounded-2xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-300/70 cursor-pointer"
                    style={{
                      color: fanOpen ? '#3a2a05' : 'rgba(255,255,255,0.7)',
                      background: fanOpen ? ACCENT : 'rgba(255,255,255,0.05)',
                      border: `1.5px solid ${fanOpen ? ACCENT : 'rgba(255,255,255,0.14)'}`,
                    }}
                  >
                    <span className="font-extrabold text-lg leading-none">+{otherSizes.length}</span>
                    <span className="text-[8px] font-extrabold tracking-wide leading-none">TAMAÑOS</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: fanOpen ? 'rotate(180deg)' : 'none', transition: 'transform .3s ease' }}>
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>

                  {/* nodos del abanico */}
                  <AnimatePresence>
                    {fanOpen &&
                      otherSizes.map((size, i) => {
                        const off = fanOffsets[i] || [0, 0];
                        const realIdx = sizes.indexOf(size);
                        // Cada tamaño se apaga solo: con 2 porciones libres
                        // la porción sigue viva y el grande de 4 no.
                        const disabled = !cabe(size) || !orderingOpen;
                        return (
                          <FanNode
                            key={size.key}
                            size={size}
                            idx={realIdx}
                            unit={unit}
                            isBest={bestSize && size.key === bestSize.key}
                            x={off[0]}
                            y={off[1]}
                            delay={i * 0.06}
                            disabled={disabled}
                            onPick={() => {
                              if (disabled) return;
                              addSize(size);
                              onFanClose && onFanClose();
                            }}
                          />
                        );
                      })}
                  </AnimatePresence>
                </div>
              )}
            </div>
            {otherSizes.length > 0 && (
              <p className="text-white/40 text-[11px] mt-2 px-1">Toca un tamaño y se agrega al instante.</p>
            )}
          </div>
        ) : (
          /* Fallback legacy: SIN presentaciones configuradas → precio del item + agregar */
          <div className="flex items-center justify-between px-1">
            <div className="text-2xl font-bold text-white">{mxn(minPrice)}</div>
            <motion.button
              type="button"
              whileTap={{ scale: 0.92 }}
              onClick={() => addSize(null)}
              disabled={!canOrder}
              aria-label={`Agregar ${title} al carrito`}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-300 ${canOrder ? 'bg-white text-gray-900 hover:bg-white/90 shadow-lg cursor-pointer' : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;
